/**
 * Simplifies a polyline using the Douglas-Peucker algorithm
 * Reduces point count while maintaining the visual shape of the line
 *
 * @param points - Array of lat/lng objects
 * @param tolerance - Maximum distance (in degrees) a point can deviate
 * @returns Simplified array of points
 */

export interface ILatLng {
    lat: number;
    lng: number;
}

const getPointDistance = (p1: ILatLng, p2: ILatLng, p3: ILatLng): number => {
    // Calculate perpendicular distance from point p2 to line p1-p3
    const x1 = p1.lat;
    const y1 = p1.lng;
    const x2 = p3.lat;
    const y2 = p3.lng;
    const x0 = p2.lat;
    const y0 = p2.lng;

    const numerator = Math.abs((y2 - y1) * x0 - (x2 - x1) * y0 + x2 * y1 - y2 * x1);
    const denominator = Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2);

    return numerator / denominator;
};

const douglasPeucker = (points: ILatLng[], tolerance: number, start: number = 0, end: number = points.length - 1): number[] => {
    if (end - start < 2) return [start, end];

    let maxDist = 0;
    let maxIdx = 0;

    for (let i = start + 1; i < end; i++) {
        const dist = getPointDistance(points[start], points[i], points[end]);
        if (dist > maxDist) {
            maxDist = dist;
            maxIdx = i;
        }
    }

    if (maxDist > tolerance) {
        const left = douglasPeucker(points, tolerance, start, maxIdx);
        const right = douglasPeucker(points, tolerance, maxIdx, end);
        return [...left.slice(0, -1), ...right];
    } else {
        return [start, end];
    }
};

/**
 * Simplify polyline points using Douglas-Peucker algorithm
 * @param points - Array of points with lat/lng
 * @param tolerance - Higher = more simplification (0.0001 to 0.001 typical)
 * @returns Simplified points array
 */
export const simplifyPolyline = (points: ILatLng[], tolerance: number = 0.0003): ILatLng[] => {
    if (!points || points.length <= 2) return points;

    const indices = douglasPeucker(points, tolerance);
    const simplified = indices.map(idx => points[idx]);

    return simplified;
};

// Cache for simplified polylines per zoom level to avoid recalculation on zoom changes
const simplificationCache = new Map<string, { zoomLevel: number; simplified: ILatLng[] }[]>();

/**
 * Simplify based on zoom level - more aggressive simplification at lower zooms
 * CACHED: Results are memoized per point set to avoid Douglas-Peucker recalculation
 * @param points - Array of points
 * @param zoomLevel - Google Maps zoom level (1-21)
 * @returns Simplified points
 */
export const simplifyPolylineByZoom = (points: ILatLng[], zoomLevel: number): ILatLng[] => {
    if (!points || points.length <= 2) return points;

    // Create a hash of the points array to use as cache key
    // Use first, last, and length to create a lightweight identifier
    const pointsHash = `${points.length}_${points[0]?.lat}_${points[points.length - 1]?.lat}`;
    const cacheKey = pointsHash;

    // Check if we have cached results for this point set
    let cachedResults = simplificationCache.get(cacheKey);
    if (cachedResults) {
        // Look for existing result at this zoom level
        const existing = cachedResults.find(r => r.zoomLevel === zoomLevel);
        if (existing) {
            return existing.simplified;
        }
    }

    // Tolerance increases as zoom decreases (more aggressive at lower zoom)
    let tolerance = 0.0003; // Default for high zoom

    if (zoomLevel < 10) tolerance = 0.001;
    else if (zoomLevel < 12) tolerance = 0.0008;
    else if (zoomLevel < 14) tolerance = 0.0005;
    else if (zoomLevel < 16) tolerance = 0.0004;

    const simplified = simplifyPolyline(points, tolerance);

    // Store in cache
    if (!cachedResults) {
        simplificationCache.set(cacheKey, []);
        cachedResults = simplificationCache.get(cacheKey)!;
    }
    cachedResults.push({ zoomLevel, simplified });

    // Keep cache bounded - only store last 3 zoom levels per point set
    if (cachedResults.length > 3) {
        cachedResults.shift();
    }

    return simplified;
};
